import { useEffect, useState } from 'react'
import { ClaudeConfigFields, type ClaudeConfigValue } from './ClaudeConfig'
import { McpConnectDialog, type McpConnectTarget } from './McpConnectDialog'
import {
  api,
  type ClaudeConfig,
  type HarnessInfo,
  type HarnessKind,
  type McpHealth,
  type McpOAuthConnectionInfo,
} from '../lib/api'
import { harnessCopy } from '../lib/harness'

const EMPTY: ClaudeConfigValue = {
  claude_settings_json: null,
  claude_md: null,
  mcp_json: null,
  skills: {},
  env_vars: {},
}

/**
 * What applies, said up front, for a harness that is not Claude Code: the
 * same fields are stored for every harness, but each reads them its own way,
 * and Claude Code's settings.json is not one any other harness reads.
 */
function otherHarnessNote(harness: string, name: string): string {
  if (harness === 'antigravity') {
    return (
      'Antigravity reads the MCP servers, the skills and the instructions — written ' +
      'to ~/.gemini/AGENTS.md — plus the environment variables. Claude Code’s ' +
      'permissions file does not apply to it, so it is not offered here.'
    )
  }
  return `Claude Code’s permissions file does not apply to ${name}, so it is not offered here.`
}

/**
 * Project- or session-level Claude Code config, in a modal of its own.
 *
 * The same four editors the global Settings screen uses, at a narrower scope
 * — `load`/`save` are the only thing that differs between "this project" and
 * "this session", so the dialog does not need to know which one it is.
 *
 * Another harness gets the same dialog minus Claude Code's settings.json
 * editor, which it never reads: offering it there invited permission rules
 * that silently did nothing.
 */
export function ClaudeConfigDialog({
  harness = 'claude_code',
  harnesses,
  title,
  note,
  load,
  save,
  onClose,
  onSaved,
  mcpConnect,
}: {
  /** The project's harness. Claude Code's dialog is the one it always was. */
  harness?: HarnessKind
  harnesses?: HarnessInfo[] | null
  title: string
  note: string
  load: () => Promise<ClaudeConfig>
  save: (input: ClaudeConfig) => Promise<unknown>
  onClose: () => void
  onSaved?: () => void
  /**
   * Lets the MCP tab offer "Connect" for a server needing OAuth. Session
   * scope relays through that specific session; project scope has no one
   * session in hand, so the backend picks any one of the caller's own
   * running sessions in this project to carry it.
   */
  mcpConnect?: Extract<McpConnectTarget, { scope: 'session' | 'project' }>
}) {
  const isClaude = harness === 'claude_code'
  const [value, setValue] = useState<ClaudeConfigValue>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [connecting, setConnecting] = useState<string | null>(null)
  const [connections, setConnections] = useState<McpOAuthConnectionInfo[]>([])

  const loadConnections = () =>
    void api.mcpOAuthConnections().then(setConnections).catch(() => setConnections([]))

  useEffect(loadConnections, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    load()
      .then((config) => {
        if (!cancelled) setValue(config)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // `load` is a fresh closure each render by design (it closes over the
    // project/session id); re-running it on every render would refetch on
    // every keystroke, so it is deliberately not in the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSave = async () => {
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await save(value)
      setNotice('Saved. Restart a harness in this project to pick it up.')
      onSaved?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  /**
   * The relay looks the server up in the *saved* config — `claude mcp login`
   * runs inside the container against whatever `sessions.ensure_session`
   * last wrote there from the database, not whatever is still sitting in
   * this dialog's own state. Typing a new server and pressing Connect before
   * Save used to relay against a server that, as far as the container was
   * concerned, did not exist yet — no authorization link ever showed up, and
   * the eventual timeout blamed the wrong thing ("may not need OAuth at
   * all"). Saving first closes that gap.
   */
  const onConnectMcp = mcpConnect
    ? async (name: string) => {
        setBusy(true)
        setError(null)
        try {
          await save(value)
          setConnecting(name)
        } catch (err) {
          setError(err instanceof Error ? err.message : String(err))
        } finally {
          setBusy(false)
        }
      }
    : undefined

  /**
   * Same "save what's about to be checked" reasoning as onConnectMcp above.
   * Errors are left to propagate — McpEditor's own runCheck already turns a
   * rejection into a banner, so catching here would just show it twice.
   */
  const onCheckMcp = mcpConnect
    ? async (): Promise<McpHealth[]> => {
        await save(value)
        return mcpConnect.scope === 'session'
          ? api.checkMcpHealth(mcpConnect.projectId, mcpConnect.session)
          : api.checkMcpHealthForProject(mcpConnect.projectId)
      }
    : undefined

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card modal modal--wide" onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <p className="hint">{note}</p>
        {!isClaude && (
          <p className="hint">{otherHarnessNote(harness, harnessCopy(harness, harnesses).name)}</p>
        )}

        {error && <div className="banner error">{error}</div>}
        {notice && <div className="banner">{notice}</div>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <ClaudeConfigFields
            value={value}
            onChange={setValue}
            claudeMdHint={
              isClaude
                ? 'Added to CLAUDE.md for this scope, alongside anything set above it'
                : 'Added to the instructions for this scope, alongside anything set above it'
            }
            onConnectMcp={onConnectMcp}
            onCheckMcp={onCheckMcp}
            mcpConnections={connections}
            claudeSettings={isClaude}
          />
        )}

        <div className="actions">
          <button className="primary" disabled={busy || loading} onClick={() => void onSave()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <div className="spacer" />
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {connecting && mcpConnect && (
        <McpConnectDialog
          target={mcpConnect}
          serverName={connecting}
          onClose={() => setConnecting(null)}
          onConnected={() => {
            setConnecting(null)
            loadConnections()
          }}
        />
      )}
    </div>
  )
}
